<script setup lang="ts">
import {
    AlertMessage,
    ButtonComp,
    ClickIconRenderer,
    ContentComp,
    DateComp,
    DisplayBool,
    DisplayTimespan,
    GridComp,
    InputBool,
    InputInt,
    InputNullableDateTime,
    InputNullableInt,
    InputNullableTimespan,
    InputText,
    ModalComp,
    SavedChangesMessage,
    TooltipWarn,
    UnsavedChangesMessage,
} from '@/components';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '@/components/Grid/Classes';
import { BibAssignPreview, BibAssignSort, BlockedBib, Category, ReportBibAssignPreviewRequest, type Eventinfo } from '@/objects/generated';
import { $ti, $tm } from '@/locize';
import { computed, ref, watch } from 'vue';
import type { BibAssignBaseTableRow, CategoryWithBibAssignFields, GroupWithBibAssignFields } from './BibAssignBaseTableRow';
import BibAssignFromToTime from './BibAssignFromToTime.vue';
import { addSecondsToDate, addTimespanToDate } from '@/utilities/dateutils';
import { propertyof, swapInList } from '@/utilities';
import type { EntityState } from '@/objects/generated/enums/EntityState';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import GridColumnOptions, { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import WizardComp from '@/components/Wizard/WizardComp.vue';
import { useHelper, useTaskLoader, useDialog } from '@/composeables';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { showPdfFromStreamInNewWindow } from '@/utilities/utils';
import { useEventCommonStoreRef, useToastStore, useValidationStoreRef } from '@/stores';
import BibAssignPreviewView from './BibAssignPreviewView.vue';
import { CreateNumbersFromPattern } from '@/utilities/searchutils';
import { BibAssignDao } from '@/dao';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import BibAssignSortPage from './BibAssignSortPage.vue';
import { featureIcons } from '@/components/Menu/featureicons';
import CategoryLabels from '../Categories/CategoryLabels.vue';

const event = defineModel<Eventinfo>({ required: true });
const categories = defineModel<Array<CategoryWithBibAssignFields>>('categories', { required: true });
const groups = defineModel<Array<GroupWithBibAssignFields>>('groups', { required: true });
const blockedBibs = defineModel<Array<BlockedBib>>('blockedBibs', { required: true });
const bibAssignSort = defineModel<Array<BibAssignSort>>('bibAssignSort', { required: true });

const { AddLoader, LoadState } = useTaskLoader();
const { AddLoader: AssignAddLoader, LoadState: AssignLoadState, ClearLoaders: AssignClearLoaders } = useTaskLoader();

const props = defineProps<{
    /**
     * If we should show the "Back" button in the modal
     */
    saveCallback: () => void;

    /**
     * If true, we will skip the wizard part and open the table only and show more warnings
     * This is used when the bibs are already assigned and we just want to show the table.
     * @default false
     */
    hasBibAssignRules: boolean | null;
}>();

const basewizardDefinition = new WizardContainer();
basewizardDefinition.WizardPages = [
    new WizardPageDefinition('settings-additional', ''),
    new WizardPageDefinition('table', ''),
    new WizardPageDefinition('preview', ''),
    new WizardPageDefinition('seeding', ''),
];
const basewizard = ref<InstanceType<typeof WizardComp> | null>(null);

const additionalWizard = ref<InstanceType<typeof WizardComp> | null>(null);
const additionalWizardDefinition = new WizardContainer();
additionalWizardDefinition.WizardPages = [
    new WizardPageDefinition('assignstarttimes', ''),
    new WizardPageDefinition('sortlistby', ''),
    new WizardPageDefinition('intervalstartquestion', ''),
    new WizardPageDefinition('intervalperiod', ''),
    new WizardPageDefinition('settings-additional-reservequestion', ''),
    new WizardPageDefinition('settings-additional-reservetypequestion', ''),
];

// We default this one to todays date
const firstStartTime = ref<Date | null>(null);

// If we already have set starttimes we want to show different info on starttime page
const haveStartTimesSet = computed(() => {
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;
    return list.some((x) => x.BackingValue.StartTime != null);
});

// Reset the start times i the base table
const resetStartTimes = () => {
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;
    return list.forEach((x) => {
        x.BackingValue.StartTime = null;
    });
};

// The interval for interval start
const intervalStartTimespan = ref<ITimeSpan | null>(null);

// Ref to the first start time input to focus it when needed
const firstStartTimeInput = ref<HTMLInputElement | null>(null);

const { Races, Groups, Categories } = useEventCommonStoreRef();

const { HasDuplicateEmitTags } = useValidationStoreRef();

// If we should keep the assigned bibs or reassign them
const keepassigned = ref<boolean>(true);

// If we should overwrite starttimes on assinged bibs
const overwriteStarttimes = ref<boolean>(false);

// If we should shuffle the bibs or not
const shuffle = ref<boolean>(false);

// Helper property for when we save
const bibassignSettingsSaved = ref<boolean>(false);

// Timeout for the bibassign settings saved message
let bibAssignSettingsSavedTimeout: number | null = null;

// Is set when there are changes that need to be saved
const hasChanges = computed(
    () =>
        (event.value?.EntityState ?? 'Unchanged') !== 'Unchanged' ||
        categories.value?.some((c) => (c.EntityState ?? 'Unchanged') !== 'Unchanged') ||
        groups.value?.some((g) => (g.EntityState ?? 'Unchanged') !== 'Unchanged') ||
        blockedBibs.value?.some((b) => (b.EntityState ?? 'Unchanged') !== 'Unchanged') ||
        bibAssignSort.value?.some((b) => (b.EntityState ?? 'Unchanged') !== 'Unchanged'),
);

watch(hasChanges, (newVal) => {
    emit('hasChanges', newVal);
});

const emit = defineEmits<{
    /**
     * This is emitted every time the tabs change, but will only be true if the user have filled all neccessary fields
     * and the bibs can be assigned.
     */
    (e: 'assignable', canAssign: boolean): void;

    /**
     * This is emitted when the seeding page is shown.
     */
    (e: 'isTablePage', state: boolean): void;

    /**
     * This is emitted when the seeding page is shown.
     */
    (e: 'isSeedingPage', state: boolean): void;

    /**
     * This is emitted when the hasChanges state changes.
     */
    (e: 'hasChanges', state: boolean): void;

    /**
     * This is emitted when the bibs have been assigned and published.
     */
    (e: 'assigned'): void;

    /**
     * This is emitted when the user leaves a page in the wizard.
     */
    (e: 'leftPage', page: string): void;
}>();

/** Properties of a list item */
declare interface IListProperties {
    Sort: number | null;
    EntityState: EntityState;
}

/**
 * Moves a row in the grid up or down.
 * @param direction The direction to move the row.
 * @param sort The sort order of the row to move.
 * @param list The list of rows in the grid.
 * @param gridToRefresh The grid to refresh after moving the row.
 */
const moveRowInGrid = async (
    direction: 'up' | 'down',
    sort: number,
    list: IListProperties[],
    gridToRefresh: IGridCompExposed<GroupWithBibAssignFields | CategoryWithBibAssignFields>,
) => {
    const clicked = list.find((x) => x.Sort == sort);

    let filterFn = (x: IListProperties) => (x.Sort ?? 0) > sort && x.EntityState != 'Deleted';
    let reduceFn = (selected: IListProperties, current: IListProperties) =>
        (current.Sort ?? 0) < (selected.Sort ?? 0) ? current : selected;

    if (direction == 'up') {
        filterFn = (x: IListProperties) => (x.Sort || 0) < sort && x.EntityState != 'Deleted';
        reduceFn = (selected: IListProperties, current: IListProperties) =>
            (current.Sort || 0) > (selected.Sort || 0) ? current : selected;
    }

    const other = swapInList(
        list,
        clicked!,
        filterFn,
        reduceFn,
        (e) => e.Sort || 0,
        (e, v: number) => (e.Sort = v),
    );

    if (other == null) return;

    await gridToRefresh.refreshCachedData();

    recalcFromTo();

    await gridToRefresh.refreshCachedData();
};

/**
 * Move an item in the list up by one position.
 * @param sort
 * @param list
 * @param gridToRefresh
 */
const moveup = (
    sort: number,
    list: IListProperties[],
    gridToRefresh: IGridCompExposed<GroupWithBibAssignFields | CategoryWithBibAssignFields>,
) => moveRowInGrid('up', sort, list, gridToRefresh);

/**
 * Moves an item in the list down by one position.
 * @param sort
 * @param list
 * @param gridToRefresh
 */
const movedown = (
    sort: number,
    list: IListProperties[],
    gridToRefresh: IGridCompExposed<GroupWithBibAssignFields | CategoryWithBibAssignFields>,
) => moveRowInGrid('down', sort, list, gridToRefresh);

/**
 * Every time a cell is changed in the grid
 * @param row
 * @param columnId
 * @param newValue
 * @param oldValue
 */
const onCellValueChanged = (
    row: CategoryWithBibAssignFields | GroupWithBibAssignFields,
    columnId: string,
    newValue: any,
    oldValue: any,
) => {
    console.log(row, columnId, newValue, oldValue);

    // If we change the StartInterval or StartDelay, we need to recalculate the FromTo values
    if (
        columnId == 'StartTime' ||
        columnId == propertyof<BibAssignBaseTableRow>('BackingValue.StartInterval') ||
        columnId == propertyof<BibAssignBaseTableRow>('BackingValue.StartDelay') ||
        columnId == propertyof<BibAssignBaseTableRow>('BackingValue.ReserveBibs') ||
        columnId == propertyof<BibAssignBaseTableRow>('BackingValue.BibTo') ||
        columnId == propertyof<BibAssignBaseTableRow>('BackingValue.BibFrom')
    ) {
        // If we have a start interval, we need to recalculate the FromTo values
        recalcFromTo();
    }

    // We revalidate all the rows in the grid
    validateRows();
};

/**
 * Recalculate the start from - to on all the rows
 */
const recalcFromTo = () => {
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;
    list.sort((a, b) => (a.BackingValue.Sort ?? -1) - (b.BackingValue.Sort ?? -1));
    let lastStart: Date | null = null;
    for (let i = 0; i < list.length; i++) {
        const item = list[i];
        lastStart = item.BackingValue.StartTime ?? lastStart;
        if (item.BackingValue.BibFrom == null || item.BackingValue.BibTo == null || lastStart == null) {
            item.FromTo = { First: null, Last: null };
            continue;
        }
        const startDelay = item.BackingValue.StartDelay ?? TimeSpan.Empty();

        let actualFirst = addTimespanToDate(startDelay, lastStart) ?? lastStart;
        item.FromTo.First = addTimespanToDate(item.BackingValue.StartInterval, actualFirst);
        const numIntervals = Math.floor(
            (item.BackingValue.BibTo - item.BackingValue.BibFrom + 1) / Math.max(1, item.BackingValue.SimultaneousStarts ?? 0),
        );
        const seconds =
            item.BackingValue.StartInterval == null ? 0 : (item.BackingValue.StartInterval.TotalMilliseconds * numIntervals) / 1000;
        item.FromTo.Last = addSecondsToDate(actualFirst, seconds);
        lastStart = item.FromTo.Last;
    }
    // TODO: Do we need to add it back?
    if (event.value.Setup!.CategoryControlled) {
        categories.value = list as CategoryWithBibAssignFields[];
    } else {
        groups.value = list as GroupWithBibAssignFields[];
    }
};

/**
 * When we are done setting up the reserve bibs, we fill the table with the values
 * and go to the next page in the wizard.
 */
const onReserveBibsSetupDone = () => {
    fillReserveBibs();
    emit('leftPage', additionalWizard.value?.currentPageDefinition?.Name ?? '');
    basewizard.value?.goto(basewizardDefinition.WizardPages[1]);
};

/**
 * When we click the skip button, we just go to the next page in the wizard.
 */
const onSkipClicked = () => {
    emit('leftPage', additionalWizard.value?.currentPageDefinition?.Name ?? '');
    basewizard.value?.goto(basewizardDefinition.WizardPages[1]);
};

/**
 * Columns for the base table for both groups and categories
 */
// const gridColumnDefinitionsCommon = <T extends BibAssignBaseTableRow>(): IGridColumnOptions<T>[] => [
const gridColumnDefinitionsCommon = [
    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(propertyof<BibAssignBaseTableRow>('BackingValue.Name'), 'bibassign.category')
        .SortDisabled()
        .ReadOnly()
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('NumContestants'),
        'bibassign.contestants',
        'contestants',
        'number',
    )
        .SortDisabled()
        .ReadOnly()
        .Header(undefined, { classes: 'text-end' })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: number | null, row: BibAssignBaseTableRow) => row.WarningColumns.has('NumContestants'),
        })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(propertyof<BibAssignBaseTableRow>('BackingValue.StartTime'), 'bibassign.starttime')
        .SortDisabled()
        .Size(220, 220)
        .CellRenderer(DateComp, { format: 'DATETIMEMS3' })
        .CellEditor(InputNullableDateTime, { format: 'DATETIMEMS3', showNullSetter: true, prefillDate: event.value?.Date })
        .Header(undefined, { classes: 'text-end' })
        .CssRule({
            className: 'grid-cell-error',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.ErrorColumns.has('StartTime'),
        })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.WarningColumns.has('StartTime'),
        })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(propertyof<BibAssignBaseTableRow>('FromTo'), 'bibassign.fromto')
        .SortDisabled()
        .ReadOnly()
        .Size(150, 150)
        .CellRenderer(BibAssignFromToTime, { class: 'fs-7' })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('BackingValue.StartInterval'),
        'bibassign.interval',
    )
        .SortDisabled()
        .Size(100, 100)
        .CellEditor(InputNullableTimespan, { format: 'TIME' })
        .CellRenderer(DisplayTimespan, { format: 'TIME' })
        .Header(undefined, { classes: 'text-end' })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(propertyof<BibAssignBaseTableRow>('BackingValue.StartDelay'), 'bibassign.delay')
        .SortDisabled()
        .Size(100, 100)
        .CellEditor(InputNullableTimespan, { format: 'TIME' })
        .CellRenderer(DisplayTimespan, { format: 'TIME' })
        .Header(undefined, { classes: 'text-end' })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('ForceBibFrom'),
        'bibassign.forcebibfromshort',
        undefined,
        'number',
    )
        .SortDisabled()
        .Header(undefined, { classes: 'text-end' })
        .Size(100, 100)
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('BackingValue.BibFrom'),
        'bibassign.bibfromshort',
        undefined,
        'number',
    )
        .SortDisabled()
        .Size(80, 80)
        .Header(undefined, { classes: 'text-end' })
        .CssRule({
            className: 'grid-cell-error',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.ErrorColumns.has('BibTo'),
        })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.WarningColumns.has('BibTo'),
        })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('BackingValue.BibTo'),
        'bibassign.bibtoshort',
        undefined,
        'number',
    )
        .SortDisabled()
        .Size(80, 80)
        .Header(undefined, { classes: 'text-end' })
        .CssRule({
            className: 'grid-cell-error',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.ErrorColumns.has('BibTo'),
        })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.WarningColumns.has('BibTo'),
        })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('BackingValue.ReserveBibs'),
        'bibassign.reservebibsshort',
        undefined,
        'number',
    )
        .SortDisabled()
        .Header(undefined, { classes: 'text-end' })
        .CssRule({
            className: 'grid-cell-error',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.ErrorColumns.has('ReserveBibs'),
        })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: Date | null, row: BibAssignBaseTableRow) => row.WarningColumns.has('ReserveBibs'),
        })
        .Build(),

    new GridColumnOptionsBuilder<BibAssignBaseTableRow>(
        propertyof<BibAssignBaseTableRow>('BackingValue.ReserveBibsInFront'),
        'bibassign.reservebibsinfrontshort',
        undefined,
        'number',
    )
        .SortDisabled()
        .CellRenderer(DisplayBool)
        .CellEditor(InputBool)
        .Header(undefined, { classes: 'text-center' })
        .Build(),
];
/**
 * Set up the datasource for the categories grid
 */
const dataSourceCategories = new GridDataSource<CategoryWithBibAssignFields>({
    totalNumberOfRows: () => categories.value.length, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<CategoryWithBibAssignFields>) => {
        categories.value.sort((a, b) => (a.BackingValue.Sort ?? -1) - (b.BackingValue.Sort ?? -1));

        const slice = categories.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, categories.value, categories.value.length);
    },
});

/**
 * Columns for the categories grid
 */
const gridColumnDefinitionsCategories = [
    new GridColumnOptionsBuilder<CategoryWithBibAssignFields>(propertyof<CategoryWithBibAssignFields>('BackingValue.Sort'), '', 'sort1')
        .ReadOnly()
        .SortDisabled()
        .CellClass('px-2 py-0 pt-1')
        .CellRenderer(ClickIconRenderer, {
            action: (sort: number) => movedown(sort, categories.value, categoriesGrid.value!),
            icon: 'fa-down',
        })
        .Size(25, 25)
        .Build(),

    new GridColumnOptionsBuilder<CategoryWithBibAssignFields>(propertyof<CategoryWithBibAssignFields>('BackingValue.Sort'), '', 'sort2')
        .ReadOnly()
        .SortDisabled()
        .CellClass('px-2 py-0 pt-1')
        .CellRenderer(ClickIconRenderer, { action: (sort: number) => moveup(sort, categories.value, categoriesGrid.value!), icon: 'fa-up' })
        .Size(25, 25)
        .Build(),

    ...gridColumnDefinitionsCommon,
];

/**
 * Set up the grid for the categories
 */
const gridSetupCategories = new GridSetup<CategoryWithBibAssignFields>({
    rowIdGetter: (row: CategoryWithBibAssignFields) => row.BackingValue.ID.toString(),
    datasource: dataSourceCategories,
    GridColumnDefinition: new GridColumnDefinition<CategoryWithBibAssignFields>({
        CommonColumnOptions: {
            sortable: false,
            filterable: true,
            editable: true,
        },
        ColumnOptions: gridColumnDefinitionsCategories,
    }),
});

/** the grid for categories */
const categoriesGrid = ref<IGridCompExposed<GroupWithBibAssignFields> | null>(null);

/**
 * Set up the datasource for the groups grid
 */
const dataSourceGroups = new GridDataSource<GroupWithBibAssignFields>({
    totalNumberOfRows: () => groups.value.length, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<GroupWithBibAssignFields>) => {
        groups.value.sort((a, b) => (a.BackingValue.Sort ?? -1) - (b.BackingValue.Sort ?? -1));

        const slice = groups.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, groups.value, groups.value.length);
    },
});

/**
 * Columns for the groups grid
 */
const gridColumnDefinitionsGroups = [
    new GridColumnOptionsBuilder<GroupWithBibAssignFields>(propertyof<GroupWithBibAssignFields>('Sort'), '', 'sort1')
        .ReadOnly()
        .SortDisabled()
        .CellClass('px-2 py-0 pt-1')
        .CellRenderer(ClickIconRenderer, { action: (sort: number) => movedown(sort, groups.value, groupsGrid.value!), icon: 'fa-down' })
        .Size(25, 25)
        .Build(),

    new GridColumnOptionsBuilder<GroupWithBibAssignFields>(propertyof<GroupWithBibAssignFields>('Sort'), '', 'sort2')
        .ReadOnly()
        .SortDisabled()
        .CellClass('px-2 py-0 pt-1')
        .CellRenderer(ClickIconRenderer, { action: (sort: number) => moveup(sort, groups.value, groupsGrid.value!), icon: 'fa-up' })
        .Size(25, 25)
        .Build(),

    ...gridColumnDefinitionsCommon,
];

/**
 * Set up the grid for the groups
 */
const gridSetupGroups = new GridSetup<GroupWithBibAssignFields>({
    rowIdGetter: (row: GroupWithBibAssignFields) => row.BackingValue.ID.toString(),
    datasource: dataSourceGroups,
    GridColumnDefinition: new GridColumnDefinition<GroupWithBibAssignFields>({
        CommonColumnOptions: {
            sortable: false,
            filterable: true,
            editable: true,
        },
        ColumnOptions: gridColumnDefinitionsGroups,
    }),
});

/** the grid for groups */
const groupsGrid = ref<IGridCompExposed<GroupWithBibAssignFields> | null>(null);

// The first bibnumber we start on
const firstBibNumber = ref<number | null>(null);

/* for the toolbar */
const additionalPercent = ref(0);
const additionalMin = ref(0);
const fillReserveBibs = () => {
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;
    list.forEach((x) => {
        x.BackingValue.ReserveBibs = Math.max(additionalMin.value, Math.floor((x.NumContestants ?? 0) * (additionalPercent.value / 100)));
    });
    (event.value.Setup!.CategoryControlled ? categoriesGrid.value : groupsGrid.value)?.refreshCachedData();
};

export type FillHelperOption =
    | 'AGEDESCGENDERMALEFIRST'
    | 'AGEDESCGENDERFEMALEFIRST'
    | 'AGEGENDERMALEFIRST'
    | 'AGEGENDERFEMALEFIRST'
    | 'GENDERAGEMALEFIRST'
    | 'GENDERAGEFEMALEFIRST'
    | 'GENDERAGEFEMALEFIRSTDESC'
    | 'GENDERAGEMALEFIRSTDESC'
    | 'KEEPCURRENT';

/**
 * The ref for the innput for setting blocked bibs
 */
const blockedBibsString = computed({
    get: () =>
        blockedBibs.value
            .filter((x) => x.EntityState != 'Deleted')
            .map((x) => x.Bib)
            .join(','),
    set: (val: string) => {
        const current = CreateNumbersFromPattern(val);

        // We check the blockedBibs if there is any that are ADDED that are NOT in the current and then we remove them¨
        const _blockedBibs = blockedBibs.value.filter((x) => {
            if (x.EntityState == 'Added' && !current.has(x.Bib)) {
                return false;
            }
            return true;
        });

        // We check the blocked bib if there is any that are still there that are not in the current and then we mark dem as DELETE
        _blockedBibs.forEach((x) => {
            if (x.EntityState != 'Deleted' && !current.has(x.Bib)) {
                x.Delete();
            } else if (x.EntityState == 'Deleted' && current.has(x.Bib)) {
                x.DeleteUndo();
            }
        });

        // We add the new blocked bibs that are in the current, but not in the blockedBibs
        current.forEach((x) => {
            if (!_blockedBibs.some((b) => b.Bib == x)) {
                const newBib = BlockedBib.FromJsValues({
                    Bib: x,
                    EntityState: 'Added',
                    EventID: event.value.ID,
                });

                _blockedBibs.push(newBib);
            }
        });

        blockedBibs.value = [..._blockedBibs];
    },
});

/**
 * Preview the bib assignment with the current settings. in a table
 */
const previewTable = async () => {
    await props.saveCallback();

    emit('leftPage', basewizard.value?.currentPageDefinition?.Name ?? '');
    basewizard.value?.goto(basewizardDefinition.WizardPages[2]);

    bibassignSettingsSaved.value = true;
    if (bibAssignSettingsSavedTimeout) {
        clearTimeout(bibAssignSettingsSavedTimeout);
    }
    bibAssignSettingsSavedTimeout = window.setTimeout(() => {
        bibassignSettingsSaved.value = false;
    }, 5000);
};

/**
 * Create a PDF report of the bib assignment preview with graphics.
 * This will show the bibs in a graphical way.
 */
const previewWithGraphics = async () => {
    await AddLoader(async () => {
        const { stream } = await BibAssignDao.BibAssignPreviewWithGraphics(
            event.value.ID,
            ReportBibAssignPreviewRequest.FromJsValues({
                CategoryIDs: Categories.value.map((c) => c.ID),
                FederationID: event.value.FederationID,
                GroupIDs: Groups.value.map((g) => g.ID),
                KeepAssigned: keepassigned.value,
                OverwriteStarttimes: overwriteStarttimes.value,
                RaceIDs: Races.value.map((r) => r.ID),
                Shuffle: shuffle.value,
            }),
        );
        if (stream == null) {
            useToastStore().addError('bibassign.error_reading_report');
            return;
        }

        try {
            await showPdfFromStreamInNewWindow(stream);
        } catch (e) {
            useToastStore().addError((e as Error).message);
        }
    }, '');
};

/**
 * Custom columns for the preview table.
 */
const previewCustomColumns = (columns: Array<GridColumnOptions<BibAssignPreview>>) => {
    // We want to remove all the columns with the seeding 1-9
    const filteredColumns = columns.filter((c) => !c.dataField.startsWith('Seeding'));

    // We loop through all columns and disable sorting and filter
    filteredColumns.forEach((c) => {
        c.sortable = false;
        c.filterable = false;

        if (c.dataField == 'ID') {
            c.maxWidth = 100;
            c.minWidth = 100;
            c.resizable = false;
        } else if (c.dataField == 'Blocked' || c.dataField == 'Bib') {
            c.maxWidth = 80;
            c.minWidth = 80;
            c.resizable = false;
            c.headerComponentParams = { classes: 'text-center' };
        } else if (c.dataField == 'StartTime') {
            c.minWidth = 150;
            c.maxWidth = 150;
            c.resizable = false;
            c.headerComponentParams = { classes: 'text-end' };
        } else if (c.dataField == 'Sort') {
            c.maxWidth = 80;
            c.minWidth = 80;
            c.resizable = false;
            c.headerComponentParams = { classes: 'text-end' };
        }
    });

    return filteredColumns;
};

/**
 * Assign and save the bibs to the contestants.
 */
const assignAndPublish = async () => {
    try {
        const res = await BibAssignDao.BibAssignBuild(event.value.ID, keepassigned.value, shuffle.value, overwriteStarttimes.value);
        return res?.Success ?? false;
    } catch (ex) {
        useToastStore().addError('bibassign.error');
        return false;
    }
};

/**
 * Dialog to confirm assigning the bibs
 */
const assignDialog = ref<InstanceType<typeof ModalComp> | null>(null);

/**
 * This will open the assign dialog to ask for confirmation on assigning bibs.
 */
const openAssignDialog = async () => {
    assignDialog.value?.show();
};

/**
 * Autofill the table based on the current settings.
 */
const autofill = () => {
    const doAutoFill = () => {
        AutofillBibs();
        setStartingTimes();

        recalcFromTo();

        validateRows();

        (event.value.Setup!.CategoryControlled ? categoriesGrid.value : groupsGrid.value)?.refreshCachedData();
    };

    if (event.value.Setup!.CategoryControlled) {
        doAutoFill();
    } else {
        useDialog().YesNo({
            title: 'bibassign.autofill',
            body: 'bibassign.autofill_question',
            yesCallback: async () => {
                doAutoFill();
                return true;
            },
        });
    }
};

/**
 * Set the starting intervals
 */
const setStartingTimes = () => {
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;

    // If we have intervals and delay we add them on each line
    list.forEach((x, i) => {
        x.BackingValue.StartInterval = intervalStartTimespan.value ?? x.BackingValue.StartInterval;

        if (i == 0 && firstStartTime.value !== null) {
            x.BackingValue.StartTime = new Date(firstStartTime.value);
        }
    });
};

/**
 * Recalculate the bib values.
 */
const AutofillBibs = () => {
    const blocked = blockedBibs.value.filter((x) => x.EntityState != 'Deleted').map((x) => x.Bib);
    let bib = 1;
    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;

    // If we have first bibnumber higher than 1 we set the first item of the list to force that number
    if (firstBibNumber.value != null && firstBibNumber.value > 1 && list.length > 0) {
        list[0].ForceBibFrom = firstBibNumber.value;
    }

    list.forEach((x) => {
        if (x.ForceBibFrom != null) {
            bib = x.ForceBibFrom;
        }
        if ((x.NumContestants ?? 0) + (x.BackingValue.ReserveBibs ?? 0) == 0) {
            x.BackingValue.BibFrom = 0;
            x.BackingValue.BibTo = 0;
            return;
        }

        x.BackingValue.BibFrom = bib;
        x.BackingValue.BibTo = bib + (x.NumContestants ?? 0) - 1 + (x.BackingValue.ReserveBibs ?? 0);

        // check if we are blocked
        for (let i = x.BackingValue.BibFrom; i <= x.BackingValue.BibTo; i++) {
            if (blocked.includes(i)) {
                x.BackingValue.BibTo++;
            }
        }

        bib = x.BackingValue.BibTo + 1;
        while (blocked.includes(bib)) {
            bib++;
        }
    });

    (event.value.Setup!.CategoryControlled ? categoriesGrid.value : groupsGrid.value)?.refreshCachedData();
};

// Helper property to determine if we have allready visited the table page.
// We use this to make sure we run autmatically autorun only once
let tablePageInitialized = false;

/**
 * Whenever a tab in the wizard is shown we can do some processing
 * @param tab
 */
const onTabShown = (tab: WizardPageDefinition) => {
    emit('assignable', tab.Name == 'table' || tab.Name == 'preview' || tab.Name == 'seeding');
    emit('isTablePage', tab.Name == 'table');
    emit('isSeedingPage', tab.Name == 'seeding');

    if (tab.Name === 'intervalperiod') {
        // We set the default interval to 30 seconds.
        intervalStartTimespan.value = TimeSpan.FromSeconds(30);
    }

    const seriesfilled = (event.value.Setup!.CategoryControlled ? categories.value : groups.value).some(
        (x) => x.BackingValue.BibFrom != null && x.BackingValue.BibTo != null,
    );

    // If we dont have any rules allready saved on groups or categories, AND we dont have series filled AND this is the first time we open the table page we run autofill automatically
    // to fill in the values of the options from the wizard
    if (tab.Name == 'table') {
        if (!props.hasBibAssignRules && !tablePageInitialized && !seriesfilled) {
            autofill();
            tablePageInitialized = true;
        } else {
            recalcFromTo();

            validateRows();
        }
    }

    useHelper().setFocus();
};

/**
 * Assigns a priority to the categories based on the given type.
 * @param type
 */
const assignCategoryPriority = (type: FillHelperOption | null, previewCategories?: CategoryWithBibAssignFields[]) => {
    if (type == null) return;

    const categoriesToUse = previewCategories ?? categories.value;

    if (
        type == 'AGEGENDERMALEFIRST' ||
        type == 'AGEGENDERFEMALEFIRST' ||
        type == 'AGEDESCGENDERMALEFIRST' ||
        type == 'AGEDESCGENDERFEMALEFIRST'
    ) {
        categoriesToUse.sort((a, b) => {
            let aAge = a.BackingValue.AgeFrom ?? 0;
            let bAge = b.BackingValue.AgeFrom ?? 0;
            let aGender = a.BackingValue.Gender ?? 'm';
            let bGender = b.BackingValue.Gender ?? 'm';

            // We want to first sort by age then by gender
            if (aAge != bAge) {
                if (type == 'AGEDESCGENDERMALEFIRST' || type == 'AGEDESCGENDERFEMALEFIRST') {
                    return bAge - aAge;
                } else {
                    return aAge - bAge;
                }
            } else {
                if (type == 'AGEGENDERFEMALEFIRST' || type == 'AGEDESCGENDERFEMALEFIRST') {
                    return aGender.localeCompare(bGender);
                } else {
                    return bGender.localeCompare(aGender);
                }
            }
        });
    } else if (
        type == 'GENDERAGEMALEFIRST' ||
        type == 'GENDERAGEFEMALEFIRST' ||
        type == 'GENDERAGEFEMALEFIRSTDESC' ||
        type == 'GENDERAGEMALEFIRSTDESC'
    ) {
        categoriesToUse.sort((a, b) => {
            let aAge = a.BackingValue.AgeFrom ?? 0;
            let bAge = b.BackingValue.AgeFrom ?? 0;
            let aGender = a.BackingValue.Gender ?? 'm';
            let bGender = b.BackingValue.Gender ?? 'm';

            // We want to first sort by age then by gender
            if (aGender != bGender) {
                return (
                    (aGender.localeCompare(bGender) < 0 ? -1 : 1) *
                    (type == 'GENDERAGEFEMALEFIRST' || type == 'GENDERAGEFEMALEFIRSTDESC' ? 1 : -1)
                );
            } else {
                return type == 'GENDERAGEFEMALEFIRSTDESC' || type == 'GENDERAGEMALEFIRSTDESC' ? bAge - aAge : aAge - bAge;
            }
        });
    } else if (type == 'KEEPCURRENT') {
        // We do nothing
    }
    if (!previewCategories) {
        categoriesToUse.forEach((x, v) => {
            x.Sort = (v + 1) * 10; // We set the sort to 10, 20, 30, etc.
        });

        categoriesGrid.value?.refreshCachedData();
    }
};

// preview for category priority assignment
const categoryPriorityPreview = computed<Map<FillHelperOption, Category[]>>(() => {
    const result = new Map<FillHelperOption, Category[]>();

    const types: FillHelperOption[] = [
        'AGEDESCGENDERMALEFIRST',
        'AGEDESCGENDERFEMALEFIRST',
        'AGEGENDERMALEFIRST',
        'AGEGENDERFEMALEFIRST',
        'GENDERAGEMALEFIRST',
        'GENDERAGEFEMALEFIRST',
        'GENDERAGEFEMALEFIRSTDESC',
        'GENDERAGEMALEFIRSTDESC',
        'KEEPCURRENT',
    ];

    types.forEach((type) => {
        const previewCategories = [...categories.value];
        assignCategoryPriority(type, previewCategories);
        result.set(
            type,
            previewCategories.map((c) => c.BackingValue),
        );
    });

    return result;
});

const warnings = ref<string[]>([]);
const errors = ref<string[]>([]);

/**
 * This will validate the rows in the grid.
 * We iterate all the rows and add the error column to the row, and trigger rerender that will make the CssRule set the cell state
 */
const validateRows = () => {
    const newWarnings: string[] = [];
    const newErrors: string[] = [];

    const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;

    // We need to know if we have interval start or not. If it is interval start we assume that items with the same StartTime is an error
    // If there is no interval start we mark the items with conflicting StartTime as an warning
    const hasIntervalStart = list.some((x) => x.BackingValue.StartInterval != null && x.BackingValue.StartInterval.TotalMilliseconds > 0);

    list.forEach((x) => {
        // TODO: We check if there is any conflicting time period
        removeRowStatus(x, hasIntervalStart ? 'error' : 'warning', 'StartTime');
        if (x.BackingValue.StartTime) {
            // We need to loop through all the other items
            const conflict =
                list.filter(
                    (y) => y.BackingValue.StartTime != null && y.BackingValue.ID != x.BackingValue.ID && isStartTimeConflict(x, y),
                ) ?? [];

            if (conflict?.length > 0) {
                addRowStatus(x, hasIntervalStart ? 'error' : 'warning', 'StartTime');
                const conflictNames = conflict.map((c) => c.BackingValue.Name).join(', ');
                if (hasIntervalStart) {
                    newErrors.push(
                        $ti('bibassign.error_conflicting_starttime', {
                            Name: x.BackingValue.Name,
                            Conflicts: conflictNames,
                        }),
                    );
                } else {
                    newWarnings.push(
                        $ti('bibassign.warning_conflicting_starttime', {
                            Name: x.BackingValue.Name,
                            Conflicts: conflictNames,
                        }),
                    );
                }
            }
        }

        let rowBibRange = getRowBibRange(x);

        // We show a warning in the BibTo if the number of bibs is less than the number of contestants + the number of reserve ( reserve numbers are expected to be empty at this point, if they are filled by reopening it shold show warning in the reserve bibs column)
        // This mean that there is not enough bibs in the series to accomodate both the number of contestants AND the number of reservenumbers wanted
        removeRowStatus(x, 'warning', 'ReserveBibs');
        if (x.NumContestants + (x.BackingValue.ReserveBibs ?? 0) > rowBibRange.Count) {
            addRowStatus(x, 'warning', 'ReserveBibs');
            newWarnings.push(
                $ti('bibassign.warning_not_enough_bibs_when_including_reserve', {
                    Name: x.BackingValue.Name,
                }),
            );
        }

        // We show a warning in the BibTo if the number of bibs is less than the number of contestants
        // This mean that there is not enough bibs in the series to accomodate both the number of contestants AND the number of reservenumbers wanted
        removeRowStatus(x, 'warning', 'NumContestants');
        if (x.NumContestants > rowBibRange.Count) {
            addRowStatus(x, 'warning', 'NumContestants');
            newWarnings.push(
                $ti('bibassign.warning_not_enough_bibs', {
                    Name: x.BackingValue.Name,
                }),
            );
        }

        // We show a warning in both the bibFrom and bibTo if the bibTo is less than  the bibFrom
        removeRowStatus(x, 'warning', 'BibFrom');
        removeRowStatus(x, 'warning', 'BibTo');
        if (rowBibRange.Count < 1) {
            addRowStatus(x, 'warning', 'BibFrom');
            addRowStatus(x, 'warning', 'BibTo');
            newWarnings.push(
                $ti('bibassign.warning_bibfrom_bibto_invalid', {
                    Name: x.BackingValue.Name,
                }),
            );
        }

        // We show an error in both the BibTo and BibFrom on all the rows where the BibRange conflicts with another range
        removeRowStatus(x, 'error', 'BibFrom');
        removeRowStatus(x, 'error', 'BibTo');
        if (rowBibRange.Count > 0) {
            const conflictingRows = list.filter(
                (y) =>
                    y.BackingValue.ID != x.BackingValue.ID &&
                    y.BackingValue.BibFrom != null &&
                    y.BackingValue.BibTo != null &&
                    y.BackingValue.BibFrom <= rowBibRange.BibTo &&
                    y.BackingValue.BibTo >= rowBibRange.BibFrom &&
                    y.BackingValue.BibFrom > 0 &&
                    y.BackingValue.BibTo > 0,
            );

            if (conflictingRows.length > 0) {
                addRowStatus(x, 'error', 'BibFrom');
                addRowStatus(x, 'error', 'BibTo');
                newErrors.push(
                    $ti('bibassign.error_conflicting_bibrange', {
                        Name: x.BackingValue.Name,
                    }),
                );
            }
        }
    });

    warnings.value = newWarnings;
    errors.value = newErrors;

    // Rerender the grids to show the errors
    if (event.value.Setup!.CategoryControlled) {
        categoriesGrid.value?.refreshCachedData();
    } else {
        groupsGrid.value?.refreshCachedData();
    }
};

/**
 * Helper method to add a status to a row.
 * This will add a warning or error status to the row based on the columnId.
 * @param row
 * @param status
 * @param columnId
 */
const addRowStatus = (row: CategoryWithBibAssignFields | GroupWithBibAssignFields, status: 'warning' | 'error', columnId: string) => {
    if (status == 'error') {
        row.ErrorColumns.add(columnId);
    } else {
        row.WarningColumns.add(columnId);
    }
};

/**
 * Helper method to remove the status from a row.
 * @param row
 * @param status
 * @param columnId
 */
const removeRowStatus = (row: CategoryWithBibAssignFields | GroupWithBibAssignFields, status: 'warning' | 'error', columnId: string) => {
    if (status == 'error') {
        row.ErrorColumns.delete(columnId);
    } else {
        row.WarningColumns.delete(columnId);
    }
};

/**
 * Helper method to calculate the total time for a row.
 * This will calculate the total time for the row based on the StartDelay, StartInterval and the number of bibs in the row.
 * @param row
 */
const getRowTotalTime = (row: CategoryWithBibAssignFields | GroupWithBibAssignFields) => {
    // We need to calculate the
    const totalBibs = (row.BackingValue.BibTo ?? 0) - (row.BackingValue.BibFrom ?? 0) + 1 + (row.BackingValue.ReserveBibs ?? 0); // The total number of bibs in the row
    const totalBibTime = TimeSpan.FromMilliseconds(totalBibs * (row.BackingValue.StartInterval?.TotalMilliseconds ?? 0)); // The total time for the bibs in the row

    return TimeSpan.FromMilliseconds(
        (row.BackingValue.StartDelay?.TotalMilliseconds ?? 0) +
            (row.BackingValue.StartInterval?.TotalMilliseconds ?? 0) +
            totalBibTime.TotalMilliseconds,
    );
};

/**
 * Helper method to get the bib range for a row.
 * This will return the BibFrom, BibTo and Count for the row.
 * @param row
 */
const getRowBibRange = (row: CategoryWithBibAssignFields | GroupWithBibAssignFields) => {
    if (row.BackingValue.BibFrom == null || row.BackingValue.BibTo == null) {
        return {
            BibFrom: 0,
            BibTo: 0,
            Count: 0,
        };
    }

    const bibFrom = row.BackingValue.BibFrom ?? 0;
    const bibTo = row.BackingValue.BibTo ?? 0;

    return {
        BibFrom: bibFrom,
        BibTo: bibTo,
        Count: bibTo - bibFrom + 1, // The total number of bibs in the row
    };
};

/**
 * Return the starttime - endtime period for a row
 * If the row dont have StartTime we return null for both startTime and endTime since we cant calculate it.
 * @param row
 */
const getRowStartTimeEndTime = (row: CategoryWithBibAssignFields | GroupWithBibAssignFields) => {
    let startTime: Date | null = null;
    let endTime: Date | null = null;
    if (!row.BackingValue.StartTime) {
        return { startTime, endTime };
    }

    return {
        startTime: row.BackingValue.StartTime,
        endTime: addTimespanToDate(getRowTotalTime(row), row.BackingValue.StartTime) ?? row.BackingValue.StartTime,
    };
};

const isStartTimeConflict = (
    row: CategoryWithBibAssignFields | GroupWithBibAssignFields,
    otherRow: CategoryWithBibAssignFields | GroupWithBibAssignFields,
) => {
    if (!row.BackingValue.StartTime || !otherRow.BackingValue.StartTime) {
        return false;
    }

    // first we get the start and end time for both rows
    const rowTimes = getRowStartTimeEndTime(row);
    const otherRowTimes = getRowStartTimeEndTime(otherRow);

    if (rowTimes.startTime == null || rowTimes.endTime == null || otherRowTimes.startTime == null || otherRowTimes.endTime == null) {
        return false; // If any of the rows don't have a valid start or end time, we can't check for conflicts
    }

    // We concider a conflict if the start time or the end time is between the start and end time of the other row

    // We check if the start time of the row is between the start and end time of the other row
    if (
        (rowTimes.startTime >= otherRowTimes.startTime && rowTimes.startTime < otherRowTimes.endTime) ||
        (otherRowTimes.startTime >= rowTimes.startTime && otherRowTimes.startTime < rowTimes.endTime)
    ) {
        return true; // There is a conflict
    }
    return false; // No conflict
};

/**
 * When the settings are saved, we check if the event is valid and then proceed to the next step in the wizard.
 */
defineExpose({
    shown() {
        const list = event.value.Setup!.CategoryControlled ? categories.value : groups.value;
        const missingOrDuplicate = list.some((x) => x.Sort == null || list.filter((y) => y.Sort == x.Sort).length > 1);
        if (missingOrDuplicate) {
            // Assign priority if it is not filled or duplicated
            list.sort((a, b) => (a.Sort ?? -1) - (b.Sort ?? -1));
            let v = 0;
            list.forEach((x) => {
                v = Math.max(v, x.Sort ?? 1);
                x.Sort = v;
                v++;
            });
        }
        useHelper().setFocus();
    },

    /**
     * Go directly to the table page in the wizard.
     */
    gotoTable() {
        basewizard.value?.goto(basewizardDefinition.WizardPages[1]);
    },

    /**
     * Go directly to the seeding page in the wizard.
     */
    gotoSeeding() {
        basewizard.value?.goto(basewizardDefinition.WizardPages[3]);
    },

    /**
     * Will build and show the preview table with the current settings.
     */
    showPreview: () => previewTable(),

    /**
     * Will build and show the preview with graphics.
     */
    printPreviewReport: async () => await previewWithGraphics(),

    /**
     * This will open the assign dialog to ask for confirmation on assigning bibs.
     */
    openAssignDialog: () => openAssignDialog(),

    /**
     * Goto a specific page in either the base or additional wizard.
     * @param pageName
     */
    goto: (pageName: string) => {
        const page = basewizardDefinition.WizardPages.find((p) => p.Name == pageName);
        if (page) {
            basewizard.value?.goto(page);
        } else {
            const additionalPage = additionalWizardDefinition.WizardPages.find((p) => p.Name == pageName);
            if (additionalPage) {
                // if we are not on this in the outer wizard we need to go there first
                basewizard.value?.goto(basewizardDefinition.WizardPages.find((p) => p.Name == 'settings-additional')!);
                // go to the page in that wizard
                additionalWizard.value?.goto(additionalPage);
            } else {
                console.warn(`Page with name ${pageName} not found in either base or additional wizard`);
            }
        }
    },
});

/**
 * Go to the next page in the additional wizard and save history
 */
const goNextAndSaveHistory = () => {
    emit('leftPage', additionalWizard.value?.currentPageDefinition?.Name ?? '');
    additionalWizard.value?.next();
};

/**
 * Go to the specified page in the additional wizard and save history
 * @param pageidx
 */
const goToAndSaveHistory = (pageidx: number) => {
    emit('leftPage', additionalWizard.value?.currentPageDefinition?.Name ?? '');
    additionalWizard.value?.goto(additionalWizardDefinition.WizardPages[pageidx]);
};

/**
 * Go to the specified page in the base wizard and save history
 * @param pageidx
 */
const goToAndSaveHistoryInBaseWizard = (pageidx: number) => {
    emit('leftPage', additionalWizard.value?.currentPageDefinition?.Name ?? '');
    basewizard.value?.goto(basewizardDefinition.WizardPages[pageidx]);
};
</script>
<template>
    <ContentComp
        class="h-100 p-0"
        :fill-content="true"
        :load-state="LoadState"
        :show-as-modal="true"
    >
        <SavedChangesMessage :visible="bibassignSettingsSaved" />

        <ModalComp
            ref="assignDialog"
            title="bibassign.assignconfirm"
        >
            <template #content>
                <ContentComp class="p-3">
                    <div class="mb-3">
                        <span v-html="$tm('bibassign.assignconfirmmessage')"></span>
                    </div>
                    <div class="mb-1">
                        <span v-html="$tm('bibassign.reservebibs_info')"></span>
                    </div>
                    <div class="d-flex gap-2 mb-1">
                        <InputInt v-model="event.ReserveBibsFrom" /> -
                        <InputInt v-model="event.ReserveBibsTo" />
                    </div>
                    <div class="d-flex">
                        <InputBool
                            v-model="keepassigned"
                            :label="$tm('bibassign.keepassigned')"
                        />
                    </div>
                    <div class="d-flex">
                        <InputBool
                            v-model="overwriteStarttimes"
                            :label="$tm('bibassign.overwritestarttimes')"
                            :tooltip="$tm('bibassign.overwritestarttimesdescription')"
                        />
                    </div>
                    <div class="d-flex mt-2">
                        <InputBool
                            v-model="shuffle"
                            :label="$tm('bibassign.shuffle')"
                            :tooltip="$tm('bibassign.shuffledescription')"
                        />
                    </div>
                </ContentComp>
            </template>
            <template #footer>
                <div class="d-flex justify-content-end gap-2">
                    <ButtonComp
                        button-type="Secondary"
                        icon="fa-times"
                        @click="() => assignDialog?.hide()"
                    >
                        {{ $tm('button.cancel') }}
                    </ButtonComp>
                    <ButtonComp
                        :loadstate="AssignLoadState"
                        button-type="Primary"
                        icon="fa-check"
                        @click="
                            () => {
                                AssignClearLoaders();
                                AssignAddLoader(
                                    async () => {
                                        await props.saveCallback();
                                        const success = await assignAndPublish();
                                        assignDialog?.hide();
                                        if (success) {
                                            emit('assigned');
                                        }
                                    },
                                    'bibassign.assigning',
                                    featureIcons.BIBS,
                                );
                            }
                        "
                    >
                        {{ $tm('bibassign.assign') }}
                    </ButtonComp>
                </div>
            </template>
        </ModalComp>

        <WizardComp
            :useGivenSpace="true"
            :wizardDefinition="basewizardDefinition"
            :showPrevNext="false"
            ref="basewizard"
            class="h-100"
            :show-header="false"
            @tab-shown="onTabShown"
            :startOnPageIndex="hasBibAssignRules ? 1 : 0"
        >
            <template #wizardPage-settings-additional>
                <div class="d-flex flex flex-column h-100">
                    <WizardComp
                        :use-given-space="true"
                        :wizardDefinition="additionalWizardDefinition"
                        :showPrevNext="false"
                        class="flex-fill h-100"
                        :show-header="false"
                        ref="additionalWizard"
                        @tab-shown="onTabShown"
                    >
                        <!-- index: 0,  We need to define a start and interval. This should only be available if there is NOT set any startingnumber on the Category/Group that is selected -->
                        <template #wizardPage-assignstarttimes>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <template v-if="haveStartTimesSet">
                                    <span
                                        class="display-3"
                                        v-html="$tm('bibassign.starttimes_already_set', '', true)"
                                    ></span>
                                    <small v-html="$tm('bibassign.starttimes_already_set_reset_question', '', true)"></small>
                                    <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                        <ButtonComp
                                            button-type="Secondary"
                                            class="btn-lg"
                                            @click="resetStartTimes()"
                                        >
                                            {{ $tm('bibassign.reset_starttimes') }}
                                        </ButtonComp>
                                        <ButtonComp
                                            button-type="Primary"
                                            class="btn-lg"
                                            @click="
                                                () => {
                                                    firstStartTime = null;
                                                    if (event.Setup!.CategoryControlled) {
                                                        goToAndSaveHistory(1);
                                                    } else {
                                                        goToAndSaveHistoryInBaseWizard(1);
                                                    }
                                                }
                                            "
                                        >
                                            {{ $tm('bibassign.continue_with_starttimes') }}
                                        </ButtonComp>
                                    </div>
                                </template>
                                <template v-else>
                                    <span
                                        class="display-3"
                                        v-html="$tm('bibassign.assignstarttimes', '', true)"
                                    ></span>
                                    <div class="d-flex justify-content-center mb-5 pb-5 bt-5 pt-5 gap-5">
                                        <span class="pe-4">
                                            <InputNullableDateTime
                                                autofocus
                                                class="form-control-lg"
                                                format="DATETIME"
                                                v-model="firstStartTime"
                                                :prefill-date="event.Date ?? null"
                                                ref="firstStartTimeInput"
                                            />
                                        </span>
                                    </div>

                                    <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                        <ButtonComp
                                            button-type="Secondary"
                                            class="btn-lg"
                                            @click="
                                                () => {
                                                    firstStartTime = null;
                                                    if (event.Setup!.CategoryControlled) {
                                                        goToAndSaveHistory(1);
                                                    } else {
                                                        goToAndSaveHistoryInBaseWizard(1);
                                                    }
                                                }
                                            "
                                        >
                                            {{ $tm('bibassign.nostarttimes') }}
                                        </ButtonComp>
                                        <ButtonComp
                                            button-type="Primary"
                                            class="btn-lg"
                                            icon="fa-check"
                                            @click="
                                                () => {
                                                    if (event.Setup!.CategoryControlled) {
                                                        goToAndSaveHistory(1);
                                                    } else {
                                                        goToAndSaveHistoryInBaseWizard(1);
                                                    }
                                                }
                                            "
                                        >
                                            {{ $tm('button.ok') }}
                                        </ButtonComp>
                                    </div>
                                </template>
                            </div>
                        </template>

                        <!-- index: 1,  How we want to sort the list initially -->
                        <template #wizardPage-sortlistby>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <span
                                    class="display-3"
                                    v-html="$tm('bibassign.sortorderquestion', '', true)"
                                ></span>

                                <div class="d-flex flex-wrap justify-content-center mb-3 bt-5 pt-5 gap-5 overflow-auto">
                                    <FormLayout>
                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg me-2 text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('AGEDESCGENDERMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.agedescgendermalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('AGEDESCGENDERMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('AGEDESCGENDERFEMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.agedescgenderfemalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('AGEDESCGENDERFEMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg me-2 text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('AGEGENDERMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.agegendermalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('AGEGENDERMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('AGEGENDERFEMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.agegenderfemalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('AGEGENDERFEMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('GENDERAGEFEMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.genderagefemalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('GENDERAGEFEMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('GENDERAGEMALEFIRST');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.genderagemalefirst') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('GENDERAGEMALEFIRST')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('GENDERAGEMALEFIRSTDESC');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.genderagemalefirstdesc') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('GENDERAGEMALEFIRSTDESC')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>

                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Secondary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('GENDERAGEFEMALEFIRSTDESC');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.genderagefemalefirstdesc') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('GENDERAGEFEMALEFIRSTDESC')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>
                                        <FormLayoutRow>
                                            <ButtonComp
                                                button-type="Primary"
                                                class="btn-lg text-nowrap"
                                                style="min-width: 390px"
                                                @click="
                                                    () => {
                                                        assignCategoryPriority('KEEPCURRENT');
                                                        goToAndSaveHistory(2);
                                                    }
                                                "
                                            >
                                                {{ $tm('bibassign.keepcurrentorder') }}
                                            </ButtonComp>
                                            <CategoryLabels
                                                :cats="categoryPriorityPreview.get('KEEPCURRENT')!"
                                                :maxLabels="12"
                                                :breakEveryN="6"
                                                class="text-start"
                                            />
                                        </FormLayoutRow>
                                    </FormLayout>
                                </div>
                            </div>
                        </template>

                        <!-- index: 2 We need determine if it is interval starts or mass start -->
                        <template #wizardPage-intervalstartquestion>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <span
                                    class="display-3"
                                    v-html="$tm('bibassign.isintervalstartquestion', '', true)"
                                ></span>

                                <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                    <ButtonComp
                                        button-type="Primary"
                                        icon="fa-check"
                                        class="btn-lg me-2"
                                        @click="goToAndSaveHistory(3)"
                                    >
                                        {{ $tm('button.yes') }}
                                    </ButtonComp>
                                    <ButtonComp
                                        button-type="Secondary"
                                        class="btn-lg"
                                        @click="goToAndSaveHistory(4)"
                                    >
                                        {{ $tm('button.no') }}
                                    </ButtonComp>
                                </div>
                            </div>
                        </template>

                        <!-- index: 3 Set up the interval period for interval start -->
                        <template #wizardPage-intervalperiod>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <span
                                    class="display-3"
                                    v-html="$tm('bibassign.setupintervalperiod', '', true)"
                                ></span>

                                <div class="d-flex justify-content-center mb-5 pb-5 bt-5 pt-5 gap-5">
                                    <span class="pe-4">
                                        <InputNullableTimespan
                                            autofocus
                                            class="form-control-lg"
                                            format="TIME"
                                            v-model="intervalStartTimespan"
                                        />
                                    </span>
                                </div>

                                <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                    <ButtonComp
                                        button-type="Primary"
                                        class="btn-lg"
                                        icon="fa-check"
                                        @click="goNextAndSaveHistory()"
                                    >
                                        {{ $tm('button.ok') }}
                                    </ButtonComp>
                                </div>
                            </div>
                        </template>

                        <!-- index: 4 Do you want to use reserve bibs question? -->
                        <template #wizardPage-settings-additional-reservequestion>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <span
                                    class="display-3"
                                    v-html="$tm('bibassign.reservebibsbyquestion', '', true)"
                                ></span>
                                <small v-html="$tm('bibassign.reservebibsbyquestiondescription', '', true)"></small>
                                <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                    <ButtonComp
                                        button-type="Primary"
                                        icon="fa-check"
                                        class="btn-lg me-2"
                                        @click="goToAndSaveHistory(5)"
                                    >
                                        {{ $tm('button.yes') }}
                                    </ButtonComp>
                                    <ButtonComp
                                        button-type="Secondary"
                                        class="btn-lg"
                                        @click="goToAndSaveHistoryInBaseWizard(1)"
                                    >
                                        {{ $tm('button.no') }}
                                    </ButtonComp>
                                </div>
                            </div>
                        </template>

                        <!-- index: 5 How to assigne reserve bibs. With pct or fixed number -->
                        <template #wizardPage-settings-additional-reservetypequestion>
                            <div class="text-center h-100 d-flex flex-column justify-content-center">
                                <span
                                    class="display-3 pt-5 mt-5"
                                    v-html="$tm('bibassign.reservebibsbytype', '', true)"
                                ></span>
                                <div class="d-flex justify-content-center mb-3 bt-5 gap-5">
                                    <span class="d-flex justify-content-center mt-5">
                                        <span class="pe-4">
                                            <InputInt
                                                class="form-control-lg w-25 text-center mx-auto"
                                                icon=""
                                                posttext="%"
                                                v-model="additionalPercent"
                                            />
                                        </span>
                                    </span>

                                    <span class="d-flex justify-content-center mt-5">
                                        <span class="pe-4">
                                            <InputInt
                                                class="form-control-lg w-25 text-center mx-auto"
                                                icon=""
                                                :label="$tm('bibassign.minreservebibs')"
                                                v-model="additionalMin"
                                            />
                                        </span>
                                    </span>
                                </div>
                                <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                                    <ButtonComp
                                        button-type="Primary"
                                        class="btn-lg"
                                        icon="fa-check"
                                        @click="onReserveBibsSetupDone"
                                    >
                                        {{ $tm('button.ok') }}
                                    </ButtonComp>
                                </div>
                            </div>
                        </template>
                    </WizardComp>

                    <div class="px-5 pb-3">
                        <ButtonComp
                            button-type="Custom"
                            :title="$tm('bibassign.skipwizard')"
                            @click="onSkipClicked"
                            class="float-end"
                        >
                            {{ $tm('button.skip') }}
                        </ButtonComp>
                    </div>
                </div>
            </template>

            <!-- This is the outer wizard -->
            <!-- index: 1  The view with the actual table to set up -->
            <template #wizardPage-table>
                <div class="d-flex flex-column flex-nowrap h-100 gap-2 py-3">
                    <UnsavedChangesMessage v-if="hasChanges" />

                    <!--this is for debug-->
                    <div class="w-25 d-none">
                        <FormLayout>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.firststarttime"></FormLayoutLabel>
                                <InputNullableDateTime
                                    format="DATETIME"
                                    v-model="firstStartTime"
                                />
                            </FormLayoutRow>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.generalinterval"></FormLayoutLabel>
                                <InputNullableTimespan
                                    format="TIME"
                                    v-model="intervalStartTimespan"
                                />
                            </FormLayoutRow>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.additionalpercent"></FormLayoutLabel>
                                <InputInt
                                    icon=""
                                    posttext="%"
                                    v-model="additionalPercent"
                                />
                            </FormLayoutRow>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.additionalmin"></FormLayoutLabel>
                                <InputInt
                                    icon=""
                                    posttext=""
                                    v-model="additionalMin"
                                />
                            </FormLayoutRow>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.blockedbibs"></FormLayoutLabel>
                                <InputText
                                    v-model.lazy="blockedBibsString"
                                    icon=""
                                    posttext=""
                                    placeholder="1,2,3,6-9"
                                />
                            </FormLayoutRow>
                            <FormLayoutRow>
                                <FormLayoutLabel text="bibassign.firsbibnumber"></FormLayoutLabel>
                                <InputNullableInt
                                    icon=""
                                    posttext=""
                                    v-model="firstBibNumber"
                                />
                            </FormLayoutRow>
                        </FormLayout>
                    </div>

                    <FormLayout :label-width="0">
                        <FormLayoutRow>
                            <!-- <ButtonComp
                                button-type="Secondary"
                                icon="fa-arrows-rotate"
                                class="me-3"
                                >{{ $tm('bibassign.releasebibs') }}</ButtonComp
                            > -->
                            <InputBool
                                label="bibassign.autoassign"
                                tooltip="bibassign.autoassigntooltip"
                                v-model="event.SignupSettings!.AutoAssignBibs"
                                id="bibassign-autoassign"
                            />
                            <InputBool
                                label="bibassign.autoassignemittags"
                                tooltip="bibassign.autoassignemittagstip"
                                v-model="event.Setup!.AutoAssignEmitTags"
                                id="bibassign-autoassignemittags"
                                class="ms-3"
                            />
                            <TooltipWarn
                                v-if="HasDuplicateEmitTags && event.Setup!.AutoAssignEmitTags"
                                :text="$tm('bibassign.autoassignemittagswarning', '', true)"
                                class="mt-n1"
                            />

                            <ButtonComp
                                class="btn text-center"
                                icon="fa-wand-sparkles"
                                :button-type="'Secondary'"
                                :loadstate="LoadState"
                                @click="autofill"
                            >
                                {{ $tm('bibassign.autofill') }}
                            </ButtonComp>

                            <FormLayoutLabel class="ms-3">{{ $tm('bibassign.blocked_bibs') }}</FormLayoutLabel>
                            <InputText
                                v-model.lazy="blockedBibsString"
                                icon=""
                                posttext=""
                                class="flex-fill"
                                placeholder="1,2,3,6-9"
                            />
                        </FormLayoutRow>
                    </FormLayout>

                    <div
                        class="flex-grow-1"
                        style="height: 1px"
                    >
                        <!--1px to make it grow and not be max-->
                        <template v-if="event.Setup!.CategoryControlled">
                            <GridComp
                                :gridSetup="gridSetupCategories"
                                ref="categoriesGrid"
                                resize-strategy="sizeColumnsToFit"
                                @cellValueChanged="onCellValueChanged"
                            ></GridComp>
                        </template>
                        <template v-else>
                            <GridComp
                                :gridSetup="gridSetupGroups"
                                ref="groupsGrid"
                                resize-strategy="sizeColumnsToFit"
                                @cellValueChanged="onCellValueChanged"
                            ></GridComp>
                        </template>
                    </div>
                    <div class="overflow-auto mh-10">
                        <AlertMessage
                            type="warning"
                            :show-icon="true"
                            v-if="warnings.length"
                        >
                            <div
                                v-for="(warning, i) in warnings"
                                :key="i"
                            >
                                {{ warning }}
                            </div>
                        </AlertMessage>
                    </div>
                    <div class="overflow-auto mh-10">
                        <AlertMessage
                            type="danger"
                            :show-icon="true"
                            v-if="errors.length"
                        >
                            <div
                                v-for="(error, i) in errors"
                                :key="i"
                            >
                                {{ error }}
                            </div>
                        </AlertMessage>
                    </div>
                </div>
            </template>

            <!-- index: 2 The preview page where we show the preview table -->
            <template #wizardPage-preview>
                <BibAssignPreviewView
                    class="p-0 h-100"
                    :EventID="event.ID"
                    :KeepAssigned="keepassigned"
                    :OverwriteStartTimes="overwriteStarttimes"
                    :Shuffle="shuffle"
                    :customColumns="previewCustomColumns"
                />
            </template>

            <!-- index: 3 The preview page where we show the preview table -->
            <template #wizardPage-seeding>
                <BibAssignSortPage
                    class="p-0 h-100"
                    v-model="bibAssignSort"
                />
            </template>
        </WizardComp>
    </ContentComp>
</template>
